import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as HaptticLogo } from 'src/assets/svg/image 5.svg'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import classes from './statusModal.module.scss'

// 'delete' is the confirm-before-delete look: the error circle with a bin.
type StatusType = 'success' | 'error' | 'delete'

interface StatusModalProps {
  children?: ReactNode
  closeOnBackdrop?: boolean
  doneLabel?: string
  hideCloseButton?: boolean
  hideCountdown?: boolean
  isOpen: boolean
  // ReactNode so a caller can pin its own line breaks with <br/>.
  message?: ReactNode
  // An info line under the message (e.g. what an optional next step is).
  note?: ReactNode
  onClose: () => void
  // The left button of the two-button row; defaults to onClose. For when it
  // must do something other than the close (×) button does.
  onDiscard?: () => void
  onSecondaryAction?: () => void
  secondaryLabel?: string
  subMessage?: string
  title?: string
  type?: StatusType
}

export const StatusModal: FC<StatusModalProps> = ({
  children,
  closeOnBackdrop = false,
  doneLabel = 'Done',
  hideCloseButton = false,
  hideCountdown = false,
  isOpen,
  message,
  note,
  onClose,
  onDiscard,
  onSecondaryAction,
  secondaryLabel,
  subMessage = 'Closing tab in',
  title,
  type = 'success',
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    if (isOpen && !hideCountdown) {
      setCountdown(5)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            onClose()
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
    return undefined
  }, [isOpen, onClose, hideCountdown])

  useDisableScroll(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div
      className={classes.modalOverlay}
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        className={classes.modalContent}
        onClick={(e) => e.stopPropagation()}
        ref={ref}
      >
        <div className={classes.modalHeader}>
          <HaptticLogo className={classes.logo} />
          {!hideCloseButton && (
            <button
              className={classes.closeButton}
              onClick={onClose}
              type="button"
            >
              <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </button>
          )}
        </div>

        <div className={classes.modalBody}>
          {children || (
            <>
              <div
                className={`${classes.iconCircle} ${
                  type === 'success'
                    ? classes.successCircle
                    : classes.errorCircle
                }`}
              >
                {type === 'success' && (
                  <svg fill="none" height="64" viewBox="0 0 64 64" width="64">
                    <path
                      d="M18 32L28 42L46 24"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="5"
                    />
                  </svg>
                )}
                {type === 'delete' && (
                  <svg fill="none" height="64" viewBox="0 0 64 64" width="64">
                    <path
                      d="M14 18H50M25 18V12H39V18M19 18L22 52H42L45 18"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="4"
                    />
                  </svg>
                )}
                {type === 'error' && (
                  <svg fill="none" height="64" viewBox="0 0 64 64" width="64">
                    <path
                      d="M20 20L44 44M44 20L20 44"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="5"
                    />
                  </svg>
                )}
              </div>

              <h2 className={classes.title}>{title}</h2>

              <p className={classes.message}>{message}</p>

              {note && <p className={classes.note}>{note}</p>}
            </>
          )}
        </div>

        <div className={classes.modalFooter}>
          {hideCountdown ? (
            onSecondaryAction ? (
              <div className={classes.twoButtonRow}>
                <button
                  className={classes.discardBtn}
                  onClick={onDiscard ?? onClose}
                  type="button"
                >
                  {secondaryLabel ?? 'Discard'}
                </button>
                <button
                  className={classes.doneBtn}
                  onClick={onSecondaryAction}
                  type="button"
                >
                  {doneLabel}
                </button>
              </div>
            ) : (
              <button
                className={classes.doneBtn}
                onClick={onClose}
                type="button"
              >
                {doneLabel}
              </button>
            )
          ) : (
            <div className={classes.countdown}>
              <svg
                className={classes.spinnerIcon}
                fill="none"
                height="16"
                viewBox="0 0 16 16"
                width="16"
              >
                <circle
                  className={classes.spinnerCircle}
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
              {subMessage} {countdown}s
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
